// Framework and third-party non-UI
import React, { useMemo } from 'react';

// Redux, contexts, and local helpers/utils/modules

// Component specific modules (Component-styled, etc.)
import {
  getControlStyles,
  DropdownIndicatorContainer,
  DropdownIndicatorPath,
  DropdownIndicatorStyles,
  DropdownIndicatorSvg,
  FormatOptionContainer,
  IndicatorSeparatorStyles,
  MenuListStyles,
  MenuPortalStyles,
  MenuStyles,
  OptionFocusedStyles,
  OptionSelectedStyles,
  OptionStyles,
  PlaceholderStyles,
  SubtitleContainer,
  ValueContainerStyles
} from './ReactSelectWrapper-styled';

// App components

// Third-party components (buttons, icons, etc.)
import ReactSelect, {
  GroupBase,
  Props as ReactSelectProps,
  PropsValue,
  StylesConfig
} from 'react-select';

export type SelectOption = {
  label: string;
  subtitle?: string;
  value: string;
};

export interface SelectProps<
  IsMulti extends boolean = true,
  GroupType extends GroupBase<SelectOption> = GroupBase<SelectOption>
> extends Omit<
    ReactSelectProps<SelectOption, IsMulti, GroupType>,
    'options' | 'formatOptionLabel' | 'value' | 'styles'
  > {
  containerStyles?: React.CSSProperties;
  optionStyles?: React.CSSProperties;
  options: { label: string; subtitle?: string; value: string }[];
  selectedValue: IsMulti extends true ? string[] : string;
  dataTestOptionPrefix?: string;
}

const DropdownIndicator = () => (
  <DropdownIndicatorContainer>
    <DropdownIndicatorSvg>
      <DropdownIndicatorPath />
    </DropdownIndicatorSvg>
  </DropdownIndicatorContainer>
);

const Select = <
  IsMulti extends boolean = true,
  GroupType extends GroupBase<SelectOption> = GroupBase<SelectOption>
>({
  containerStyles = {},
  optionStyles = {},
  isSearchable = false,
  options,
  selectedValue,
  dataTestOptionPrefix,
  ...rest
}: SelectProps<IsMulti, GroupType>) => {
  // Create internal representation of available options
  const _options = useMemo(
    (): SelectOption[] =>
      options.map(option => ({
        value: option.value,
        label: option.label,
        subtitle: option.subtitle
      })),
    [options]
  );

  // Create internal representation of selected value from options
  const _selectedValue = useMemo<PropsValue<SelectOption>>(() => {
    if (rest.isMulti) {
      return _options.filter(({ value }) => selectedValue.includes(value));
    } else {
      return _options.find(({ value }) => value === selectedValue) || null;
    }
  }, [_options, rest.isMulti, selectedValue]);

  // Merge prop styles with defaults
  const _styles = useMemo<StylesConfig<SelectOption, IsMulti, GroupType>>(
    () => ({
      container: base => ({
        ...base,
        ...containerStyles
      }),
      control: (base, { isFocused }) => ({
        ...base,
        ...getControlStyles(isFocused)
      }),
      dropdownIndicator: base => ({ ...base, ...DropdownIndicatorStyles }),
      indicatorSeparator: base => ({ ...base, ...IndicatorSeparatorStyles }),
      menu: base => ({ ...base, ...MenuStyles }),
      menuList: base => ({ ...base, ...MenuListStyles }),
      menuPortal: base => ({ ...base, ...MenuPortalStyles }),
      option: (base, { isSelected, isFocused }) => ({
        ...base,
        ...OptionStyles,
        ...optionStyles,
        ...(isFocused ? OptionFocusedStyles : {}),
        ...(isSelected ? OptionSelectedStyles : {})
      }),
      placeholder: base => ({ ...base, ...PlaceholderStyles }),
      valueContainer: base => ({ ...base, ...ValueContainerStyles })
    }),
    [containerStyles, optionStyles]
  );

  // Merge components with overrides
  const _components = useMemo(
    () => ({
      DropdownIndicator,
      ...(rest.components || {})
    }),
    [rest]
  );

  const formatOptionLabel = ({ label, subtitle, value }: SelectOption) => (
    <FormatOptionContainer
      data-test={`${dataTestOptionPrefix}-${value}-option`}
    >
      <div>{label}</div>
      {!rest.isMulti && <SubtitleContainer>{subtitle}</SubtitleContainer>}
    </FormatOptionContainer>
  );

  return (
    <ReactSelect
      menuPosition="fixed"
      menuPortalTarget={document.body}
      {...rest}
      isSearchable={isSearchable}
      components={_components}
      formatOptionLabel={formatOptionLabel}
      value={_selectedValue}
      options={_options}
      styles={_styles}
    />
  );
};

export default Select;
